import { useEffect } from 'react';

/**
 * useOutsideClose — the pad's ONE "click away and it closes" rule.
 *
 * Every field panel on the prescription pad already closes this way: a
 * `mousedown` (and `touchstart`) listener on the DOCUMENT, because the panels
 * are `position: fixed` and a click can land anywhere, plus a list of elements
 * the click is allowed to land on without closing — the panel itself and the
 * control that opens it.
 *
 * The medicine box, the diagnosis dropdown, the symptom picker and the
 * All-templates list each grew their own copy of that effect; this is the same
 * rule in one place, so the Tests field did not need a fifth.
 *
 * Nothing is listened for while the panel is closed, and both listeners come
 * off on close and on unmount — a stray document listener on a pad that mounts
 * four times is exactly how a click starts closing something that is not there
 * any more.
 *
 * @param {boolean} open
 * @param {Array<{current: HTMLElement|null}>} ignoreRefs  panel + its opener
 * @param {() => void} onClose
 */
export default function useOutsideClose(open, ignoreRefs, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      for (const ref of ignoreRefs) {
        if (ref?.current?.contains?.(e.target)) return;
      }
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
    // `ignoreRefs` is a fresh array each render by construction (refs are
    // stable); depending on `open` and the callback is what decides.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose]);
}
